/**
 * Role-based Home — the entry surface of each track (Phase 1 · Step 6).
 *
 * One component, three tracks resolved from the LOGIN role (roles.ts):
 *   admin  → cross-project pulse (the aggregate is appropriate for admin)
 *   staff  → my assigned projects + work focus
 *   client → my engagement at a glance (NO money internals, NO AI/review,
 *            NO internal notes — owner-locked spine)
 *
 * Every number is derived from the engagementTemplate + ledger contract
 * (via trackDemo, which only visualises the locked gate config). HR /
 * Partner / External-Consultant fall to the reserved placeholder (their
 * dedicated screens are owner-locked later sprints).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import {
  DEMO_STAFF as ROSTER_DEMO_STAFF,
  findPerson,
  resolveHome,
  type RosterPerson,
} from '../lib/responsibilityTags';
import {
  listenTasks,
  tasksForUid,
  isOpenTask,
  type LiveTask,
} from '../lib/tasksStore';
import { Box, Btn3, Card, EmptyNote, StageRail, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import { useToast } from '../components/Toast';
import {
  seedProjects,
  DEMO_STAFF,
  adminAggregate,
  clientMoneySummary,
  inr,
  projectsForStaff,
  railOf,
  templateOf,
  type DemoProject,
} from '../lib/trackDemo';
import { listenClientProjects } from '../lib/clientProjects';
import Placeholder from './Placeholder';
import NeedsAttentionStrip from '../components/leadviews/NeedsAttentionStrip';

/**
 * ROLE-BASED HOME via per-person responsibility tags (chat-first
 * correction batch · item 4). The login role still GATES access
 * (RequireAuth / roles.ts). WHERE a permitted person lands is owner-
 * chosen per person via responsibility tags (responsibilityTags.ts):
 *   · carries `leads`            → Lead Inbox  (Khilendra)
 *   · carries `operations`       → their assigned work (the 3 ops people)
 *   · carries `admin_oversight`  → the cross-project pulse
 * The demo signed-in person is Khilendra (DEMO_STAFF.uid) → her home
 * resolves to /leads. Switch demo identity with ?who=u-kunjal etc.
 */
function demoWhoUid(): string {
  if (typeof window !== 'undefined') {
    const w = new URLSearchParams(window.location.search).get('who');
    if (w) return w;
  }
  return ROSTER_DEMO_STAFF.uid; // u-khilendra (the existing demo identity)
}

export default function Home() {
  const { role } = useAuth();
  const person = findPerson(demoWhoUid());
  const home = resolveHome(person, role);

  // Round 3 (FIX 3, #920): the work-home content must follow the PREVIEWED
  // person (?who=), not a hardcoded Khilendra.
  const previewed = person ?? findPerson(DEMO_STAFF.uid);

  // ─────────────────────────────────────────────────────────────────────
  // TASKS-FIRST (WA↔AVBP batch · item 5, owner-locked): every role's home
  // FIRST surface is that person's priority task list, ordered by their
  // responsibility. The existing role-home content renders BELOW it
  // (unchanged). The client home has no internal task list (locked
  // visibility model) — clients keep their engagement-at-a-glance view.
  // ─────────────────────────────────────────────────────────────────────

  // Owner-chosen landing for the leads person (Khilendra): tasks-first,
  // THEN a prominent Lead Inbox entry (the existing front-door content is
  // one tap away — it is no longer an instant redirect so the priority
  // task list is genuinely the first thing seen).
  if (home.home === 'leads') {
    return (
      <div style={col}>
        <TasksFirst />
        <Box
          title="Front door"
          note={home.reason}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/leads">
              <Btn3 intent="ink">Open Lead Inbox</Btn3>
            </Link>
            <Link to="/conversation">
              <Btn3 intent="ghost">Review Queue</Btn3>
            </Link>
          </div>
        </Box>
      </div>
    );
  }

  if (home.home === 'admin_pulse') {
    return <AdminHome reason={home.reason} />;
  }
  if (home.home === 'ops_work') {
    return <StaffHome reason={home.reason} person={previewed} />;
  }

  if (role === 'admin') return <AdminHome />;
  if (role === 'staff') return <StaffHome person={previewed} />;
  if (role === 'client') return <ClientHome />;
  // HR / Partner / External-Consultant — owner-locked later sprints.
  return <Placeholder name="Home" />;
}

/* ════════════════════ Tasks-first panel ════════════════════ */

/**
 * The owner-locked FIRST surface: the signed-in user's own task list.
 *
 * BUILD B (2026-05-21) — rewired from demo data to LIVE Firestore. This
 * panel was previously fed by `lib/tasks.ts` — 100% hardcoded demo rows
 * (`u-khilendra`/`u-kunjal` slugs), no Firebase. It now subscribes to the
 * REAL top-level `tasks/` collection (`lib/tasksStore.listenTasks`) and
 * filters to the signed-in user's real assignee id — their `staffId`
 * claim, which is what the AI assistant's `StaffResolver` writes into
 * `tasks/{}.assigneeUid`. Tasks created by the assistant (in-app AND over
 * WhatsApp) appear here for the person they were assigned to.
 *
 * `staffId` is null for a non-team / claim-less session (e.g. the owner
 * walking the demo tracks without a staff record). In that case the panel
 * honestly says no tasks resolve — it never fabricates demo rows.
 */
function TasksFirst() {
  const { staffId } = useAuth();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<LiveTask[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed READ (e.g. permission-denied) must NOT look like a genuinely
  // empty list — the "No open tasks" state would falsely read as "all done".
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const unsub = listenTasks(setTasks, {
      onErr: () => {
        setLoadError(true);
        toast('Could not load your tasks (check connection)');
      },
      onReady: () => setLoading(false),
    });
    return unsub;
  }, [toast]);

  // Only the signed-in user's OWN assigned, still-open tasks.
  const mine = tasksForUid(tasks, staffId).filter(isOpenTask);

  return (
    <>
    {/* Phase 3a — overdue lead follow-ups, ABOVE the task list (the strip
        renders nothing when nothing is overdue). Riding inside TasksFirst
        puts it on every team home (leads / admin / staff) in one place. */}
    <NeedsAttentionStrip />
    <Box
      title="Your tasks — priority first"
      note="Open tasks assigned to you — including ones the AI assistant created"
    >
      {loading ? (
        <ListLoading label="Loading your tasks…" rows={2} />
      ) : loadError ? (
        <EmptyNote title="Tasks could not be loaded">
          Something went wrong reading your tasks — this is not the same as
          having none. Check your connection and refresh.
        </EmptyNote>
      ) : !staffId ? (
        <EmptyNote title="No staff record on this session">
          Your tasks resolve once you sign in with a staff account. Tasks
          assigned to a staff member appear here automatically.
        </EmptyNote>
      ) : mine.length === 0 ? (
        <EmptyNote title="No open tasks">
          You have nothing assigned right now.
        </EmptyNote>
      ) : (
        <div style={col}>
          {mine.map((t) => (
            <TaskRow key={t.id} t={t} />
          ))}
        </div>
      )}
    </Box>
    </>
  );
}

function TaskRow({ t }: { t: LiveTask }) {
  // Plain-language origin: where the AI assistant was driven from.
  const via =
    t.surface === 'whatsapp'
      ? 'WhatsApp'
      : t.surface === 'portal_chat'
        ? 'Portal chat'
        : t.createdVia || t.source || 'AI assistant';
  const when =
    t.atMs != null
      ? new Date(t.atMs).toLocaleDateString([], {
          day: 'numeric',
          month: 'short',
        })
      : null;
  return (
    <Card flat className="row-card">
      <div style={rowHead}>
        <b>{t.title}</b>
        <Tag kind={t.priority === 'high' ? 'warn' : 'tag'}>
          {t.priority}
        </Tag>
        <Tag kind="val">{t.status}</Tag>
        <span style={{ marginLeft: 'auto', ...muted }}>
          via {via}
          {when ? ` · ${when}` : ''}
        </span>
      </div>
    </Card>
  );
}

/* ════════════════════ Admin track ════════════════════ */

function AdminHome({ reason }: { reason?: string }) {
  const agg = adminAggregate();
  return (
    <div style={col}>
      {reason && (
        <p style={{ ...muted, fontSize: 12, margin: 0 }}>{reason}</p>
      )}
      {/* Tasks-first: owner-locked first surface (above the pulse). */}
      <TasksFirst />
      <Box
        title="Admin · cross-project pulse"
        note="Aggregate across every active engagement — admin oversight"
      >
        <div style={statGrid}>
          <Stat k="Active projects" v={String(agg.projectCount)} />
          <Stat k="Total value" v={inr(agg.totalValueINR)} />
          <Stat k="Collected" v={inr(agg.totalPaidINR)} tone="good" />
          <Stat k="Due (gated)" v={inr(agg.totalDueINR)} tone="warn" />
          <Stat k="Open in Review" v={String(agg.openProposals)} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <Link to="/leads"><Btn3 intent="ink">Lead Inbox (front door)</Btn3></Link>
          <Link to="/clients"><Btn3 intent="ghost">All clients & projects</Btn3></Link>
          <Link to="/money"><Btn3 intent="ghost">Money & payments</Btn3></Link>
          <Link to="/workload"><Btn3 intent="ghost">Workload</Btn3></Link>
          <Link to="/conversation"><Btn3 intent="ghost">Review Queue</Btn3></Link>
        </div>
      </Box>

      <Box title="Approvals waiting on you" note="Curation gates to assignee + admin">
        {seedProjects().filter((p) => p.openProposals > 0).length === 0 ? (
          <EmptyNote title="Nothing waiting">
            No project is blocked on an admin approval right now.
          </EmptyNote>
        ) : (
          <div style={col}>
            {seedProjects().filter((p) => p.openProposals > 0).map((p) => (
              <Card key={p.pid} className="row-card">
                <div style={rowHead}>
                  <b>{p.vp} — {p.clientName}</b>
                  <Tag kind="val">{p.tier}</Tag>
                  <span style={muted}>{p.openProposals} in Review</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Link to="/conversation">
                      <Btn3 intent="grn" size="sm">Open Review Queue</Btn3>
                    </Link>
                  </span>
                </div>
                <StageRail stages={railOf(p)} />
              </Card>
            ))}
          </div>
        )}
      </Box>
    </div>
  );
}

/* ════════════════════ Staff track ════════════════════ */

function StaffHome({
  reason,
  person,
}: {
  reason?: string;
  /** The PREVIEWED person (?who=) — drives the list + title (FIX 3). */
  person?: RosterPerson | null;
}) {
  // Round 3 (FIX 3, #920): use the previewed person for BOTH the project
  // list and the title. Falls back to the demo staff when none resolved.
  const who = person ?? DEMO_STAFF;
  const mine = projectsForStaff(who.uid);
  return (
    <div style={col}>
      {reason && (
        <p style={{ ...muted, fontSize: 12, margin: 0 }}>{reason}</p>
      )}
      {/* Tasks-first: owner-locked first surface (above assigned projects). */}
      <TasksFirst />
      <Box
        title={`Staff · ${who.name}'s projects`}
        note="Projects assigned to you — you hold commit rights on these"
      >
        {mine.length === 0 ? (
          <EmptyNote title="No projects assigned">
            You have no active assigned projects.
          </EmptyNote>
        ) : (
          <div style={col}>
            {mine.map((p) => {
              const tpl = templateOf(p);
              return (
                <Card key={p.pid} className="row-card">
                  <div style={rowHead}>
                    <b>{p.vp} — {p.clientName}</b>
                    <Tag kind="val">{tpl.tier} · {tpl.displayName}</Tag>
                    {p.openProposals > 0 && (
                      <span style={muted}>{p.openProposals} to review</span>
                    )}
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <Link to={`/project/${p.pid}`}>
                        <Btn3 intent="ink" size="sm">Open project</Btn3>
                      </Link>
                      <Link to="/conversation">
                        <Btn3 intent="ghost" size="sm">Conversation</Btn3>
                      </Link>
                    </span>
                  </div>
                  <StageRail stages={railOf(p)} />
                </Card>
              );
            })}
          </div>
        )}
      </Box>
      <Box title="Quick actions" note="Composer · Day-44 follow-up">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/leads"><Btn3 intent="ink">Open Lead Inbox (front door)</Btn3></Link>
          <Link to="/project/VP656"><Btn3 intent="ghost">Open VP656 (live demo)</Btn3></Link>
          <Link to="/ancillary"><Btn3 intent="ghost">Raise an ancillary job</Btn3></Link>
        </div>
      </Box>
    </div>
  );
}

/* ════════════════════ Client track ════════════════════ */

function ClientHome() {
  const vis = visibilityFor('client');
  const { clientId } = useAuth();
  const { toast } = useToast();
  // Slice 3 — LIVE, client-scoped. The client sees ONLY their own projects'
  // engagement + money (from the authoritative per-project gateState). No demo
  // seed. With no live project, a neutral welcome.
  const [projects, setProjects] = useState<DemoProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = listenClientProjects(
      clientId,
      (state) => setProjects(state.projects),
      {
        onErr: () => toast('Could not load your engagement (check connection)'),
        onReady: () => setLoading(false),
      },
    );
    return unsub;
  }, [clientId, toast]);

  if (loading) {
    return (
      <div style={col}>
        <Box title="Your Aayatan Veda engagement">
          <ListLoading label="Loading your engagement…" rows={2} />
        </Box>
      </div>
    );
  }

  const p = projects[0];
  if (!p) {
    return (
      <div style={col}>
        <Box title="Your Aayatan Veda engagement">
          <EmptyNote title="No active engagement yet">
            Your project will appear here once it has been set up. We will
            be in touch — meanwhile you can reach us any time from the
            conversation screen.
          </EmptyNote>
          <div style={{ marginTop: 12 }}>
            <Link to="/conversation"><Btn3 intent="ink">Open conversation</Btn3></Link>
          </div>
        </Box>
      </div>
    );
  }
  const tpl = templateOf(p);
  // OWNER CORRECTION (2026-05-17): money IS client-visible — surface the
  // paid/due + "what to pay next" at a glance, across THIS client's projects.
  const summary = clientMoneySummary(projects);
  return (
    <div style={col}>
      <Box
        title="Your Aayatan Veda engagement"
        note={`${tpl.displayName}`}
      >
        <div style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
          <b style={{ color: 'var(--ink)' }}>{p.vp}</b> · Client {p.clientNo}
          <br />
          Where things stand right now:
        </div>
        <div style={{ margin: '14px 0' }}>
          <StageRail stages={railOf(p)} />
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to={`/conversation/${p.pid}`}><Btn3 intent="ink">Open conversation</Btn3></Link>
          <Link to="/journey"><Btn3 intent="ghost">Your journey & payments</Btn3></Link>
          <Link to="/report"><Btn3 intent="au">⟡ Your report</Btn3></Link>
        </div>
      </Box>

      {/* Money is client-visible (owner-locked). Paid / due + next-to-pay. */}
      <Box
        title="Your payments"
        note={`${inr(summary.totalPaidINR)} paid · ${inr(summary.totalDueINR)} outstanding`}
      >
        <div style={statGrid}>
          <Stat k="Total paid" v={inr(summary.totalPaidINR)} tone="good" />
          <Stat k="Outstanding" v={inr(summary.totalDueINR)} tone="warn" />
          <Stat k="Projects" v={String(summary.projectCount)} />
        </div>
        {summary.nextAcrossProjects && (
          <Card flat className="row-card" style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink-soft)' }}>
              <b style={{ color: 'var(--ink)' }}>To unlock your next step:</b>{' '}
              pay <b>{inr(summary.nextAcrossProjects.amountINR)}</b> —{' '}
              {summary.nextAcrossProjects.unlocks}.
            </div>
          </Card>
        )}
        <div style={{ marginTop: 12 }}>
          <Link to="/journey"><Btn3 intent="ghost">See full timeline</Btn3></Link>
        </div>
        {/* AI/review layer stays hidden (unchanged). Money is NOT hidden. */}
        {!vis.seesReviewLayer && (
          <p style={{ ...muted, marginTop: 14, fontSize: 12 }}>
            You see your conversation, journey, payments and delivered
            report. The team's internal working notes and review workspace
            are not part of your view.
          </p>
        )}
      </Box>
    </div>
  );
}

/* ════════════════════ shared bits ════════════════════ */

const col: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 20 };
const rowHead: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 10,
  flexWrap: 'wrap',
};
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--ink-mute)' };
const statGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 12,
};

function Stat({
  k,
  v,
  tone,
}: {
  k: string;
  v: string;
  tone?: 'good' | 'warn';
}) {
  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: '12px 14px',
        background: 'var(--surface)',
      }}
    >
      <div style={{ ...muted, marginBottom: 4 }}>{k}</div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          fontWeight: 600,
          color:
            tone === 'good'
              ? 'var(--good)'
              : tone === 'warn'
                ? 'var(--warn)'
                : 'var(--ink)',
        }}
      >
        {v}
      </div>
    </div>
  );
}
